import type { LaserficheFieldSourceDetails } from "./laserficheFieldSources";
import type { LaserficheInspectField, LaserfichePutField } from "@repo/types";
import { cn } from "@repo/shared/utils";
import { useState } from "react";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { LaserficheFieldMapping } from "./LaserficheFieldMapping";
import {
  FieldMappingContext,
  type FieldMappingContextType,
} from "./FieldMappingContext";
import { useFieldMapping } from "./useFieldMapping";
import { wait } from "@repo/lib/wait";

export interface LaserficheFieldMapperProps {
  disabled?: boolean;
  availableSources: (LaserficheFieldSourceDetails & { key: string })[];
  availableFields: LaserficheInspectField[];
  value?: LaserfichePutField[];
  onChange?: (newValue: LaserfichePutField[]) => void;
  className?: string;
}

export const LaserficheFieldMapper = ({
  disabled = false,
  availableSources,
  availableFields,
  value,
  onChange,
  className,
}: LaserficheFieldMapperProps) => {
  const fieldMap = useFieldMapping(availableFields);

  const context: FieldMappingContextType = {
    disabled,
    mutableId: true,
    availableSources,
    availableFields,
    fieldMap,
  };

  const handleChange = useFutureCallback(
    (prevField: LaserfichePutField, newField: LaserfichePutField) => {
      const updatedValue = (value ?? []).slice();
      const field = updatedValue.find(
        (field) => field.fieldId === prevField.fieldId
      );
      if (field) {
        field.fieldId = newField.fieldId;
        field.sourceKey = newField.sourceKey;
        field.constantValue = newField.constantValue;
      } else {
        updatedValue.push({ ...newField });
      }

      onChange?.(updatedValue);
    }
  );

  const handleAdd = useFutureCallback(async (newField: LaserfichePutField) => {
    if (!newField.fieldId || (!newField.sourceKey && !newField.constantValue)) {
      return;
    }

    const newValue = (value ?? []).filter(
      (field) => field.fieldId !== newField.fieldId
    );
    newValue.push(newField);

    onChange?.(newValue);

    setNewField({ fieldId: 0, sourceKey: "" });

    await wait(10);
  });

  const handleDelete = useFutureCallback((oldField: LaserfichePutField) => {
    const newValue = (value ?? []).filter(
      (field) => field.fieldId !== oldField.fieldId
    );
    onChange?.(newValue);
  });

  const [newField, setNewField] = useState<LaserfichePutField>({
    fieldId: 0,
    sourceKey: "",
  });

  return (
    <FieldMappingContext.Provider value={context}>
      <div className={cn("flex flex-col gap-5", className)}>
        <div className="flex flex-col gap-2">
          {value?.map((field) => (
            <LaserficheFieldMapping
              key={field.fieldId}
              value={field}
              onChange={handleChange}
              mode="delete"
              onButtonClick={handleDelete}
            />
          ))}
        </div>
        <div className="flex flex-row gap-3 items-start">
          <p className="mt-10">New:</p>
          <LaserficheFieldMapping
            value={newField}
            onChange={(_prevValue, value) => {
              setNewField(value);
            }}
            mode="add"
            onButtonClick={handleAdd}
          />
        </div>
      </div>
    </FieldMappingContext.Provider>
  );
};
